import React, { useState } from "react";
import ReportPageShell from "../components/ReportPageShell";
import { SecondaryButton } from "../../../components/Button";
import { defaultFilters } from "../shared/filters";
import { useReconciliation } from "../shared/useReconciliation";
import { centsToUsd, formatCount } from "../shared/format";
import { downloadReconciliationCsv, reconciliationHeadline } from "../shared/reconciliationCsv";
import { ReconciliationResponse, ReconTotals } from "../api/types";

const TOOLTIP =
  "Old-vs-new reconciliation for one event, both sides computed on the SAME live data (so the gap is " +
  "pure methodology, not timing). Old-comparable = the legacy basis (completed, real-money-backed sales " +
  "only; comp/$0 and refunded excluded from gross). New = the corrected engine (== Event Summary): " +
  "refunded + comp sales stay in Gross and net back out; Net is real-money-capped per order.";

const TOTAL_ROWS: { key: keyof ReconTotals; label: string; money: boolean; highlight?: boolean }[] = [
  { key: "gross", label: "Gross Sales", money: true },
  { key: "net", label: "Net Sales", money: true, highlight: true },
  { key: "tax", label: "Tax", money: true },
  { key: "tip", label: "Tips", money: true },
  { key: "surcharge", label: "Service Fee", money: true },
  { key: "discount", label: "Discounts", money: true },
  { key: "refunds", label: "Refunds", money: true },
  { key: "orders", label: "Orders", money: false },
  { key: "transactions", label: "Transactions", money: false },
];

const fmt = (v: number, money: boolean) => (money ? centsToUsd(v) : formatCount(v));
const diffUsd = (n: number) => (n > 0 ? "+" : "") + centsToUsd(n);
const diffNum = (n: number) => (n > 0 ? "+" : "") + formatCount(n);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="mb-2 mt-1 text-sm font-bold uppercase tracking-wide text-gray-700">{children}</h3>
);
const Card = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-4 overflow-x-auto rounded-md bg-white p-4 shadow-sm">{children}</div>
);
const th = "px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500";
const thR = "px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500";
const td = "px-3 py-2 text-sm text-gray-800";
const tdR = "px-3 py-2 text-right text-sm tabular-nums text-gray-800";

function ReconciliationBody({ data }: { data: ReconciliationResponse }) {
  const b = data.bridge;
  // Running balance down the Gross -> Net bridge (should land exactly on Net).
  const bridgeSteps: { label: string; amount: number }[] = [
    { label: "− Tax", amount: b.tax },
    { label: "− Discounts", amount: b.discount },
    { label: "− Refund pre-tax netting", amount: b.refundPreTaxNetting },
    { label: "− Real-money cap reduction", amount: b.realMoneyCapReduction },
    { label: "− Header vs line adjustment", amount: b.headerVsLineAdjustment },
  ];
  let running = b.gross;

  return (
    <>
      {/* scope + export */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-lg font-semibold text-gray-900">
          {data.scope.eventName ?? "Event"}
          {data.scope.eventLegacyId != null && (
            <span className="ml-2 text-sm font-normal text-gray-500">legacy #{data.scope.eventLegacyId}</span>
          )}
        </div>
        <SecondaryButton onClick={() => downloadReconciliationCsv(data)}>Export to CSV</SecondaryButton>
      </div>

      {/* 2. bottom line */}
      <div className="mb-4 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-800">
        {reconciliationHeadline(data)}
      </div>

      {/* 3. side-by-side totals */}
      <Card>
        <SectionTitle>Side-by-side totals</SectionTitle>
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className={th}>Metric</th>
              <th className={thR}>Old (comparable)</th>
              <th className={thR}>New</th>
              <th className={thR}>Difference</th>
            </tr>
          </thead>
          <tbody>
            {TOTAL_ROWS.map((r) => {
              const oldV = data.oldComparable[r.key];
              const newV = data.new[r.key];
              const d = newV - oldV;
              return (
                <tr
                  key={r.key}
                  className={`border-b border-gray-100 ${r.highlight ? "bg-primary-50 font-semibold" : ""}`}
                >
                  <td className={td}>{r.label}</td>
                  <td className={tdR}>{fmt(oldV, r.money)}</td>
                  <td className={tdR}>{fmt(newV, r.money)}</td>
                  <td className={`${tdR} ${d !== 0 ? (d > 0 ? "text-emerald-700" : "text-red-700") : "text-gray-400"}`}>
                    {r.money ? diffUsd(d) : diffNum(d)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* 4. itemized differences */}
      <Card>
        <SectionTitle>Itemized difference (Σ Gross Δ = Gross gap)</SectionTitle>
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className={th}>Cause</th>
              <th className={thR}>Gross Δ</th>
              <th className={th}>Rule</th>
              <th className={thR}>Net impact</th>
              <th className={thR}>Orders</th>
              <th className={thR}>Transactions</th>
            </tr>
          </thead>
          <tbody>
            {data.differences.map((diff) => (
              <tr key={diff.category} className="border-b border-gray-100 align-top">
                <td className={td}>{diff.label}</td>
                <td className={tdR}>{diffUsd(diff.grossDelta)}</td>
                <td className={`${td} max-w-md text-xs text-gray-500`}>{diff.rule}</td>
                <td className={tdR}>{diffUsd(diff.netImpact)}</td>
                <td className={tdR}>{diffNum(diff.orders)}</td>
                <td className={tdR}>{diffNum(diff.transactions)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-gray-300 font-semibold">
              <td className={td}>Total (Gross gap / Net gap)</td>
              <td className={tdR}>{diffUsd(data.grossGap)}</td>
              <td className={td} />
              <td className={tdR}>{diffUsd(data.netGap)}</td>
              <td className={tdR} />
              <td className={tdR} />
            </tr>
          </tbody>
        </table>
      </Card>

      {/* 5. gross -> net bridge */}
      <Card>
        <SectionTitle>Gross → Net bridge (new)</SectionTitle>
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className={th}>Component</th>
              <th className={thR}>Amount</th>
              <th className={thR}>Running</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100 font-semibold">
              <td className={td}>Gross</td>
              <td className={tdR}>{centsToUsd(b.gross)}</td>
              <td className={tdR}>{centsToUsd(b.gross)}</td>
            </tr>
            {bridgeSteps.map((s) => {
              running -= s.amount;
              return (
                <tr key={s.label} className="border-b border-gray-100">
                  <td className={td}>{s.label}</td>
                  <td className={tdR}>{centsToUsd(s.amount)}</td>
                  <td className={tdR}>{centsToUsd(running)}</td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-gray-300 bg-primary-50 font-semibold">
              <td className={td}>= Net Sales</td>
              <td className={tdR}>{centsToUsd(b.net)}</td>
              <td className={tdR}>{centsToUsd(b.net)}</td>
            </tr>
          </tbody>
        </table>
      </Card>

      {/* 6. extra transactions */}
      <Card>
        <SectionTitle>Extra transactions (new counts that old drops)</SectionTitle>
        <table className="min-w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className={th}>Bucket</th>
              <th className={thR}>Count</th>
              <th className={thR}>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className={td}>Comp / no real money</td>
              <td className={tdR}>{formatCount(data.extra.compNoRealMoneyCount)}</td>
              <td className={tdR}>{centsToUsd(data.extra.compNoRealMoneyValue)}</td>
            </tr>
            <tr className="border-b border-gray-100">
              <td className={td}>Refunded-order transactions</td>
              <td className={tdR}>{formatCount(data.extra.refundedOrderCount)}</td>
              <td className={tdR}>{centsToUsd(data.extra.refundedOrderValue)}</td>
            </tr>
            <tr className="border-t-2 border-gray-300 font-semibold">
              <td className={td}>Total extra</td>
              <td className={tdR}>{formatCount(data.extra.totalCount)}</td>
              <td className={tdR}>{centsToUsd(data.extra.totalValue)}</td>
            </tr>
          </tbody>
        </table>
      </Card>
    </>
  );
}

// Parallel to the reconciliation doc: pick an event → old-vs-new financial breakdown with the
// itemized gross gap, the Gross→Net bridge, and the extra transactions, all reconciling by construction.
export default function ReconciliationReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const state = useReconciliation(filters);

  return (
    <ReportPageShell
      title="Reconciliation (Old vs New)"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      idlePrompt="Select a single event above to run the old-vs-new reconciliation."
    >
      {(data) => <ReconciliationBody data={data} />}
    </ReportPageShell>
  );
}
